import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useMessage } from '../hooks/useMessage'
import { Modal } from '../components/Modal'
import { CurrencySelect } from '../components/CurrencySelect'
import {
  TIMEOUTS,
  UNDO_WINDOW_MS,
  LIMITS,
  PRECISION,
  DEFAULT_CURRENCY,
  ACCEPTED_IMAGE_TYPES,
  EVENT_TYPES,
  EXPENSE_TYPES,
  EXPENSE_CATEGORIES,
} from '../constants'
import { formatRelativeTime } from '../utils/formatters'
import type { LocationData, EventHistoryItem } from '../types'
import './LogEvent.css'

type QuickAction = 'shower' | 'fuel' | 'water_refill' | 'grey_empty' | 'propane' | 'adblue' | 'vehicle_expense' | 'custom'

interface EventData {
  event_type: string
  data?: Record<string, unknown>
  notes?: string
  location_lat?: number
  location_lon?: number
}

interface WaterRefillFormData {
  amount: string
  isFullTank: boolean
  cost: string
  notes: string
}

interface RefuelFormData {
  amount: string
  price: string
  currency: string
  odometer: string
  isFullTank: boolean
  dashboardKmL: string
  stationName: string
  notes: string
  exchangeRate: string
  showExchangeRate: boolean
}

interface PropaneReplaceFormData {
  tankSize: string
  cost: string
  currency: string
  notes: string
  exchangeRate: string
  showExchangeRate: boolean
}

interface AdBlueRefillFormData {
  amount: string
  price: string
  currency: string
  notes: string
  exchangeRate: string
  showExchangeRate: boolean
}

interface VehicleExpenseFormData {
  expenseType: string
  category: string
  description: string
  cost: string
  currency: string
  odometer: string
  receiptId: number | null
  receiptFilename: string
  notes: string
}

interface CustomEventFormData {
  eventName: string
  notes: string
}

interface EventApiResponse {
  id: number
  event_type: string
  timestamp: string
  data?: Record<string, unknown>
  was_adjusted_to_max_capacity?: boolean
}

export function LogEvent() {
  const { isAdmin, user } = useAuth()
  const { message, showError, showSuccess } = useMessage()
  const [loading, setLoading] = useState<QuickAction | null>(null)
  const [activeForm, setActiveForm] = useState<QuickAction | null>(null)
  const [showGreyEmptyConfirm, setShowGreyEmptyConfirm] = useState(false)
  const [location, setLocation] = useState<LocationData | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [eventHistory, setEventHistory] = useState<EventHistoryItem[]>([])
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [undoConfirmEvent, setUndoConfirmEvent] = useState<EventHistoryItem | null>(null)
  const [undoing, setUndoing] = useState(false)

  const [waterRefillForm, setWaterRefillForm] = useState<WaterRefillFormData>({
    amount: '',
    isFullTank: false,
    cost: '',
    notes: ''
  })

  const [refuelForm, setRefuelForm] = useState<RefuelFormData>({
    amount: '',
    price: '',
    currency: DEFAULT_CURRENCY,
    odometer: '',
    isFullTank: false,
    dashboardKmL: '',
    stationName: '',
    notes: '',
    exchangeRate: '',
    showExchangeRate: false
  })

  const [propaneForm, setPropaneForm] = useState<PropaneReplaceFormData>({
    tankSize: '',
    cost: '',
    currency: DEFAULT_CURRENCY,
    notes: '',
    exchangeRate: '',
    showExchangeRate: false
  })

  const [adblueForm, setAdblueForm] = useState<AdBlueRefillFormData>({
    amount: '',
    price: '',
    currency: DEFAULT_CURRENCY,
    notes: '',
    exchangeRate: '',
    showExchangeRate: false
  })

  const [vehicleExpenseForm, setVehicleExpenseForm] = useState<VehicleExpenseFormData>({
    expenseType: EXPENSE_TYPES[0],
    category: '',
    description: '',
    cost: '',
    currency: DEFAULT_CURRENCY,
    odometer: '',
    receiptId: null,
    receiptFilename: '',
    notes: ''
  })
  const [uploadingReceipt, setUploadingReceipt] = useState(false)

  const [customEventForm, setCustomEventForm] = useState<CustomEventFormData>({
    eventName: '',
    notes: ''
  })

  const fetchLocation = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/dashboard')
      if (response.ok) {
        const data = await response.json()
        if (data.location) {
          setLocation(data.location)
        }
      }
    } catch (err) {
      console.error('Failed to fetch location:', err)
    }
  }, [])

  const fetchEventHistory = useCallback(async (retryCount = 0) => {
    try {
      const response = await fetch('/api/v1/events?limit=20', {
        credentials: 'same-origin',
        cache: 'no-store'
      })
      if (response.ok) {
        const data = await response.json()
        setEventHistory(data.events || [])
      } else if (response.status === 401 && retryCount < 2) {
        await new Promise(resolve => setTimeout(resolve, TIMEOUTS.AUTH_RETRY_DELAY))
        return fetchEventHistory(retryCount + 1)
      } else {
        console.error('Failed to fetch events:', response.status)
      }
    } catch (err) {
      console.error('Failed to fetch event history:', err)
    } finally {
      setLoadingHistory(false)
    }
  }, [])

  useEffect(() => {
    if (!user) return
    fetchLocation()
    fetchEventHistory()
  }, [user, fetchLocation, fetchEventHistory])

  const logEvent = async (eventData: EventData): Promise<EventApiResponse> => {
    const response = await fetch('/api/v1/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to log event')
    }

    const result: EventApiResponse = await response.json()
    fetchEventHistory()
    return result
  }

  const handleQuickAction = async (action: QuickAction) => {
    if (action === 'shower') {
      setLoading(action)

      try {
        await logEvent({ event_type: 'shower' })
        showSuccess('Shower logged!')
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to log shower')
      } finally {
        setLoading(null)
      }
      return
    }

    if (action === 'grey_empty') {
      setShowGreyEmptyConfirm(true)
      return
    }

    setActiveForm(action)
  }

  const handleGreyEmptyConfirm = async () => {
    setShowGreyEmptyConfirm(false)
    setLoading('grey_empty')

    try {
      await logEvent({ event_type: 'grey_empty' })
      showSuccess('Grey water emptied!')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to log grey water empty')
    } finally {
      setLoading(null)
    }
  }

  const closeForm = () => {
    setActiveForm(null)
    setWaterRefillForm({ amount: '', isFullTank: false, cost: '', notes: '' })
    setRefuelForm({
      amount: '', price: '', currency: DEFAULT_CURRENCY, odometer: '',
      isFullTank: false, dashboardKmL: '', stationName: '', notes: '',
      exchangeRate: '', showExchangeRate: false
    })
    setPropaneForm({
      tankSize: '', cost: '', currency: DEFAULT_CURRENCY, notes: '',
      exchangeRate: '', showExchangeRate: false
    })
    setAdblueForm({
      amount: '', price: '', currency: DEFAULT_CURRENCY, notes: '',
      exchangeRate: '', showExchangeRate: false
    })
    setVehicleExpenseForm({
      expenseType: EXPENSE_TYPES[0], category: '', description: '',
      cost: '', currency: DEFAULT_CURRENCY, odometer: '',
      receiptId: null, receiptFilename: '', notes: ''
    })
    setCustomEventForm({ eventName: '', notes: '' })
  }

  const handleWaterRefillSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!waterRefillForm.isFullTank && !waterRefillForm.amount) {
      showError('Please enter an amount or check "Full tank"')
      return
    }

    setSubmitting(true)

    try {
      const eventData: EventData = {
        event_type: 'water_refill',
        data: {
          is_full_tank: waterRefillForm.isFullTank,
          ...(waterRefillForm.amount && { amount_liters: parseFloat(waterRefillForm.amount) }),
          ...(waterRefillForm.cost && { cost: parseFloat(waterRefillForm.cost) })
        },
        ...(waterRefillForm.notes && { notes: waterRefillForm.notes }),
        ...(location && { location_lat: location.latitude, location_lon: location.longitude })
      }

      const result = await logEvent(eventData)
      if (result.was_adjusted_to_max_capacity) {
        showSuccess('Water refill logged! Amount was adjusted to max tank capacity.')
      } else {
        showSuccess('Water refill logged!')
      }
      closeForm()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to log water refill')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRefuelSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!refuelForm.amount) {
      showError('Please enter fuel amount')
      return
    }

    if (refuelForm.isFullTank && !refuelForm.dashboardKmL) {
      showError('Dashboard km/L is required for full tank refuels')
      return
    }

    setSubmitting(true)

    try {
      const eventData: EventData = {
        event_type: 'refuel',
        data: {
          amount_liters: parseFloat(refuelForm.amount),
          ...(refuelForm.price && { price: parseFloat(refuelForm.price) }),
          currency: refuelForm.currency,
          ...(refuelForm.odometer && { odometer: parseFloat(refuelForm.odometer) }),
          is_full_tank: refuelForm.isFullTank,
          ...(refuelForm.isFullTank && refuelForm.dashboardKmL && {
            dashboard_km_per_liter: parseFloat(refuelForm.dashboardKmL)
          }),
          ...(refuelForm.stationName && { station_name: refuelForm.stationName }),
          ...(refuelForm.exchangeRate && { exchange_rate: parseFloat(refuelForm.exchangeRate) })
        },
        ...(refuelForm.notes && { notes: refuelForm.notes }),
        ...(location && { location_lat: location.latitude, location_lon: location.longitude })
      }

      await logEvent(eventData)
      showSuccess('Refueling logged!')
      closeForm()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to log refueling'
      showError(errorMsg, TIMEOUTS.MESSAGE_DISMISS_LONG)
      if (errorMsg.includes('exchange_rate')) {
        setRefuelForm(prev => ({ ...prev, showExchangeRate: true }))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handlePropaneSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const eventData: EventData = {
        event_type: 'propane_replace',
        data: {
          ...(propaneForm.tankSize && { tank_size_kg: parseFloat(propaneForm.tankSize) }),
          ...(propaneForm.cost && { cost: parseFloat(propaneForm.cost) }),
          ...(propaneForm.cost && { currency: propaneForm.currency }),
          ...(propaneForm.exchangeRate && { exchange_rate: parseFloat(propaneForm.exchangeRate) })
        },
        ...(propaneForm.notes && { notes: propaneForm.notes }),
        ...(location && { location_lat: location.latitude, location_lon: location.longitude })
      }

      await logEvent(eventData)
      showSuccess('Propane replacement logged!')
      closeForm()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to log propane replacement'
      showError(errorMsg, TIMEOUTS.MESSAGE_DISMISS_LONG)
      if (errorMsg.includes('exchange_rate')) {
        setPropaneForm(prev => ({ ...prev, showExchangeRate: true }))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleAdblueSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!adblueForm.amount) {
      showError('Please enter AdBlue amount')
      return
    }

    setSubmitting(true)

    try {
      const eventData: EventData = {
        event_type: 'adblue_refill',
        data: {
          amount_liters: parseFloat(adblueForm.amount),
          ...(adblueForm.price && { price: parseFloat(adblueForm.price) }),
          ...(adblueForm.price && { currency: adblueForm.currency }),
          ...(adblueForm.exchangeRate && { exchange_rate: parseFloat(adblueForm.exchangeRate) })
        },
        ...(adblueForm.notes && { notes: adblueForm.notes }),
        ...(location && { location_lat: location.latitude, location_lon: location.longitude })
      }

      await logEvent(eventData)
      showSuccess('AdBlue refill logged!')
      closeForm()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to log AdBlue refill'
      showError(errorMsg, TIMEOUTS.MESSAGE_DISMISS_LONG)
      if (errorMsg.includes('exchange_rate')) {
        setAdblueForm(prev => ({ ...prev, showExchangeRate: true }))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type as typeof ACCEPTED_IMAGE_TYPES[number])) {
      showError('Receipt must be a JPEG or PNG image')
      return
    }

    if (file.size > LIMITS.MAX_FILE_SIZE_BYTES) {
      showError('Receipt file must be under 10MB')
      return
    }

    setUploadingReceipt(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/api/v1/receipts', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to upload receipt')
      }

      const data = await response.json()
      setVehicleExpenseForm(prev => ({
        ...prev,
        receiptId: data.id,
        receiptFilename: file.name
      }))
      showSuccess('Receipt uploaded!')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to upload receipt')
    } finally {
      setUploadingReceipt(false)
      e.target.value = ''
    }
  }

  const handleVehicleExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!vehicleExpenseForm.description) {
      showError('Please enter a description')
      return
    }

    if (!vehicleExpenseForm.cost) {
      showError('Please enter a cost')
      return
    }

    setSubmitting(true)

    try {
      const expenseData = {
        expense_type: vehicleExpenseForm.expenseType,
        description: vehicleExpenseForm.description,
        cost: parseFloat(vehicleExpenseForm.cost),
        currency: vehicleExpenseForm.currency,
        ...(vehicleExpenseForm.category && { category: vehicleExpenseForm.category }),
        ...(vehicleExpenseForm.odometer && { odometer: parseFloat(vehicleExpenseForm.odometer) }),
        ...(vehicleExpenseForm.receiptId && { receipt_id: vehicleExpenseForm.receiptId }),
        ...(vehicleExpenseForm.notes && { notes: vehicleExpenseForm.notes }),
        ...(location && { location_lat: location.latitude, location_lon: location.longitude })
      }

      const response = await fetch('/api/v1/vehicle-expenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(expenseData),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to log expense')
      }

      fetchEventHistory()
      showSuccess('Vehicle expense logged!')
      closeForm()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to log vehicle expense')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCustomEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!customEventForm.eventName.trim()) {
      showError('Please enter an event name')
      return
    }

    setSubmitting(true)

    try {
      const eventData: EventData = {
        event_type: 'custom',
        data: { event_name: customEventForm.eventName.trim() },
        ...(customEventForm.notes && { notes: customEventForm.notes }),
        ...(location && { location_lat: location.latitude, location_lon: location.longitude })
      }

      await logEvent(eventData)
      showSuccess('Custom event logged!')
      closeForm()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to log custom event')
    } finally {
      setSubmitting(false)
    }
  }

  const isUndoable = (event: EventHistoryItem): boolean => {
    const eventTime = new Date(event.timestamp).getTime()
    return (Date.now() - eventTime) < UNDO_WINDOW_MS
  }

  const handleUndo = async () => {
    if (!undoConfirmEvent) return

    setUndoing(true)

    try {
      const response = await fetch(`/api/v1/events/${undoConfirmEvent.id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to undo event')
      }

      showSuccess('Event undone!')
      setUndoConfirmEvent(null)
      fetchEventHistory()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to undo event')
    } finally {
      setUndoing(false)
    }
  }

  const getEventIcon = (eventType: string): string => {
    return EVENT_TYPES[eventType as keyof typeof EVENT_TYPES]?.icon || '📋'
  }

  const formatEventType = (eventType: string): string => {
    return EVENT_TYPES[eventType as keyof typeof EVENT_TYPES]?.label || eventType
  }

  const getEventDetails = (event: EventHistoryItem): string => {
    const details: string[] = []
    const data = event.data || {}

    switch (event.event_type) {
      case 'refuel':
        if (data.amount_liters) details.push(`${data.amount_liters}L`)
        if (data.price && data.currency) details.push(`${data.price} ${data.currency}`)
        if (data.is_full_tank) details.push('Full tank')
        break
      case 'water_refill':
        if (data.is_full_tank) details.push('Full tank')
        else if (data.amount_liters) details.push(`${data.amount_liters}L`)
        if (data.cost) details.push(`${data.cost}`)
        break
      case 'propane_replace':
        if (data.tank_size_kg) details.push(`${data.tank_size_kg}kg`)
        if (data.cost && data.currency) details.push(`${data.cost} ${data.currency}`)
        break
      case 'adblue_refill':
        if (data.amount_liters) details.push(`${data.amount_liters}L`)
        if (data.price && data.currency) details.push(`${data.price} ${data.currency}`)
        break
      case 'vehicle_expense':
        if (data.description) details.push(String(data.description))
        break
      case 'custom':
        if (data.event_name) details.push(String(data.event_name))
        break
    }

    if (event.notes) {
      const notePreview = event.notes.length > 30 ? event.notes.substring(0, 30) + '...' : event.notes
      details.push(notePreview)
    }

    return details.join(' • ') || '-'
  }

  const LocationInfo = () => location ? (
    <div className="form-group location-info">
      <span className="location-label">📍 Location:</span>
      <span className="location-coords">
        {location.latitude.toFixed(PRECISION.LOCATION)}, {location.longitude.toFixed(PRECISION.LOCATION)}
      </span>
    </div>
  ) : null

  return (
    <div className="page log-event">
      <h1>Log Event</h1>
      <p>Record activities and events for your campervan.</p>

      {message && (
        <div className={`message message--${message.type}`}>
          {message.text}
        </div>
      )}

      {!isAdmin && (
        <div className="message message--info">
          You are logged in as a guest and can only view data - not create events.
        </div>
      )}

      <section className="quick-actions-section">
        <h2>Quick Actions</h2>
        <div className="quick-actions-grid">
          {(['shower', 'fuel', 'water_refill', 'grey_empty', 'propane', 'adblue', 'vehicle_expense', 'custom'] as const).map(action => {
            const config = {
              shower: { icon: '🚿', label: loading === 'shower' ? 'Logging...' : 'Shower' },
              fuel: { icon: '⛽', label: 'Fuel' },
              water_refill: { icon: '💧', label: 'Water Refill' },
              grey_empty: { icon: '🚰', label: loading === 'grey_empty' ? 'Logging...' : 'Empty Greywater' },
              propane: { icon: '🔥', label: 'Propane' },
              adblue: { icon: '💎', label: 'AdBlue' },
              vehicle_expense: { icon: '🔧', label: 'Vehicle Expense' },
              custom: { icon: '📝', label: 'Custom' },
            }[action]

            return (
              <button
                key={action}
                className={`quick-action-btn ${!isAdmin ? 'quick-action-btn--disabled' : ''}`}
                onClick={() => handleQuickAction(action)}
                disabled={!isAdmin || loading === action}
                title={!isAdmin ? 'Only admins can create events' : undefined}
              >
                <span className="quick-action-icon">{config.icon}</span>
                <span className="quick-action-label">{config.label}</span>
              </button>
            )
          })}
        </div>
      </section>

      {/* Refueling Form */}
      {activeForm === 'fuel' && (
        <Modal title="Log Refueling" onClose={closeForm} variant="form">
          <form className="event-form" onSubmit={handleRefuelSubmit}>
            <div className="form-group">
              <label htmlFor="fuel-amount">Amount (L) *</label>
              <input
                type="number"
                id="fuel-amount"
                value={refuelForm.amount}
                onChange={(e) => setRefuelForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="e.g. 50"
                step="0.1"
                min="0"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fuel-price">Price</label>
                <input
                  type="number"
                  id="fuel-price"
                  value={refuelForm.price}
                  onChange={(e) => setRefuelForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="e.g. 500"
                  step="0.01"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="fuel-currency">Currency</label>
                <CurrencySelect
                  id="fuel-currency"
                  value={refuelForm.currency}
                  onChange={(value) => setRefuelForm(prev => ({ ...prev, currency: value }))}
                />
              </div>
            </div>

            {refuelForm.showExchangeRate && (
              <div className="form-group form-group--highlight">
                <label htmlFor="fuel-exchange-rate">Exchange Rate *</label>
                <input
                  type="number"
                  id="fuel-exchange-rate"
                  value={refuelForm.exchangeRate}
                  onChange={(e) => setRefuelForm(prev => ({ ...prev, exchangeRate: e.target.value }))}
                  placeholder="e.g. 7.45"
                  step="0.0001"
                  min="0"
                  required
                />
                <span className="form-help">Rate to convert {refuelForm.currency} to native currency</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="fuel-odometer">Odometer (km)</label>
              <input
                type="number"
                id="fuel-odometer"
                value={refuelForm.odometer}
                onChange={(e) => setRefuelForm(prev => ({ ...prev, odometer: e.target.value }))}
                placeholder="e.g. 125000"
                step="1"
                min="0"
              />
            </div>

            <div className="form-group form-group--checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={refuelForm.isFullTank}
                  onChange={(e) => setRefuelForm(prev => ({
                    ...prev,
                    isFullTank: e.target.checked,
                    dashboardKmL: e.target.checked ? prev.dashboardKmL : ''
                  }))}
                />
                <span>Full tank</span>
              </label>
            </div>

            {refuelForm.isFullTank && (
              <div className="form-group form-group--highlight">
                <label htmlFor="fuel-dashboard-kml">Dashboard km/L *</label>
                <input
                  type="number"
                  id="fuel-dashboard-kml"
                  value={refuelForm.dashboardKmL}
                  onChange={(e) => setRefuelForm(prev => ({ ...prev, dashboardKmL: e.target.value }))}
                  placeholder="e.g. 8.5"
                  step="0.1"
                  min="0"
                  required
                />
                <span className="form-help">Required for heater fuel calculation</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="fuel-station">Station name (optional)</label>
              <input
                type="text"
                id="fuel-station"
                value={refuelForm.stationName}
                onChange={(e) => setRefuelForm(prev => ({ ...prev, stationName: e.target.value }))}
                placeholder="e.g. Shell, Circle K"
              />
            </div>

            <div className="form-group">
              <label htmlFor="fuel-notes">Notes (optional)</label>
              <textarea
                id="fuel-notes"
                value={refuelForm.notes}
                onChange={(e) => setRefuelForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <LocationInfo />

            <div className="form-actions">
              <button type="button" className="btn" onClick={closeForm}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Water Refill Form */}
      {activeForm === 'water_refill' && (
        <Modal title="Log Water Refill" onClose={closeForm} variant="form">
          <form className="event-form" onSubmit={handleWaterRefillSubmit}>
            <div className="form-group">
              <label htmlFor="water-amount">Amount (L)</label>
              <input
                type="number"
                id="water-amount"
                value={waterRefillForm.amount}
                onChange={(e) => setWaterRefillForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="e.g. 50"
                step="0.1"
                min="0"
                disabled={waterRefillForm.isFullTank}
              />
            </div>

            <div className="form-group form-group--checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={waterRefillForm.isFullTank}
                  onChange={(e) => setWaterRefillForm(prev => ({
                    ...prev,
                    isFullTank: e.target.checked,
                    amount: e.target.checked ? '' : prev.amount
                  }))}
                />
                <span>Full tank</span>
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="water-cost">Cost (optional)</label>
              <input
                type="number"
                id="water-cost"
                value={waterRefillForm.cost}
                onChange={(e) => setWaterRefillForm(prev => ({ ...prev, cost: e.target.value }))}
                placeholder="e.g. 5.00"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="water-notes">Notes (optional)</label>
              <textarea
                id="water-notes"
                value={waterRefillForm.notes}
                onChange={(e) => setWaterRefillForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <LocationInfo />

            <div className="form-actions">
              <button type="button" className="btn" onClick={closeForm}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Propane Form */}
      {activeForm === 'propane' && (
        <Modal title="Log Propane Replacement" onClose={closeForm} variant="form">
          <form className="event-form" onSubmit={handlePropaneSubmit}>
            <div className="form-group">
              <label htmlFor="propane-tank-size">Tank size (kg) - optional</label>
              <input
                type="number"
                id="propane-tank-size"
                value={propaneForm.tankSize}
                onChange={(e) => setPropaneForm(prev => ({ ...prev, tankSize: e.target.value }))}
                placeholder="e.g. 11"
                step="0.1"
                min="0"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="propane-cost">Cost (optional)</label>
                <input
                  type="number"
                  id="propane-cost"
                  value={propaneForm.cost}
                  onChange={(e) => setPropaneForm(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="e.g. 200"
                  step="0.01"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="propane-currency">Currency</label>
                <CurrencySelect
                  id="propane-currency"
                  value={propaneForm.currency}
                  onChange={(value) => setPropaneForm(prev => ({ ...prev, currency: value }))}
                />
              </div>
            </div>

            {propaneForm.showExchangeRate && (
              <div className="form-group form-group--highlight">
                <label htmlFor="propane-exchange-rate">Exchange Rate *</label>
                <input
                  type="number"
                  id="propane-exchange-rate"
                  value={propaneForm.exchangeRate}
                  onChange={(e) => setPropaneForm(prev => ({ ...prev, exchangeRate: e.target.value }))}
                  placeholder="e.g. 7.45"
                  step="0.0001"
                  min="0"
                  required
                />
                <span className="form-help">Rate to convert {propaneForm.currency} to native currency</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="propane-notes">Notes (optional)</label>
              <textarea
                id="propane-notes"
                value={propaneForm.notes}
                onChange={(e) => setPropaneForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <LocationInfo />

            <div className="form-actions">
              <button type="button" className="btn" onClick={closeForm}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* AdBlue Form */}
      {activeForm === 'adblue' && (
        <Modal title="Log AdBlue Refill" onClose={closeForm} variant="form">
          <form className="event-form" onSubmit={handleAdblueSubmit}>
            <div className="form-group">
              <label htmlFor="adblue-amount">Amount (L) *</label>
              <input
                type="number"
                id="adblue-amount"
                value={adblueForm.amount}
                onChange={(e) => setAdblueForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="e.g. 10"
                step="0.1"
                min="0"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="adblue-price">Price</label>
                <input
                  type="number"
                  id="adblue-price"
                  value={adblueForm.price}
                  onChange={(e) => setAdblueForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="e.g. 50"
                  step="0.01"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="adblue-currency">Currency</label>
                <CurrencySelect
                  id="adblue-currency"
                  value={adblueForm.currency}
                  onChange={(value) => setAdblueForm(prev => ({ ...prev, currency: value }))}
                />
              </div>
            </div>

            {adblueForm.showExchangeRate && (
              <div className="form-group form-group--highlight">
                <label htmlFor="adblue-exchange-rate">Exchange Rate *</label>
                <input
                  type="number"
                  id="adblue-exchange-rate"
                  value={adblueForm.exchangeRate}
                  onChange={(e) => setAdblueForm(prev => ({ ...prev, exchangeRate: e.target.value }))}
                  placeholder="e.g. 7.45"
                  step="0.0001"
                  min="0"
                  required
                />
                <span className="form-help">Rate to convert {adblueForm.currency} to native currency</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="adblue-notes">Notes (optional)</label>
              <textarea
                id="adblue-notes"
                value={adblueForm.notes}
                onChange={(e) => setAdblueForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <LocationInfo />

            <div className="form-actions">
              <button type="button" className="btn" onClick={closeForm}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Vehicle Expense Form */}
      {activeForm === 'vehicle_expense' && (
        <Modal title="Log Vehicle Expense" onClose={closeForm} variant="form">
          <form className="event-form" onSubmit={handleVehicleExpenseSubmit}>
            <div className="form-group">
              <label htmlFor="expense-type">Type *</label>
              <select
                id="expense-type"
                value={vehicleExpenseForm.expenseType}
                onChange={(e) => setVehicleExpenseForm(prev => ({ ...prev, expenseType: e.target.value }))}
                required
              >
                {EXPENSE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="expense-description">Description *</label>
              <input
                type="text"
                id="expense-description"
                value={vehicleExpenseForm.description}
                onChange={(e) => setVehicleExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g. Oil change, brake pads"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="expense-cost">Cost *</label>
                <input
                  type="number"
                  id="expense-cost"
                  value={vehicleExpenseForm.cost}
                  onChange={(e) => setVehicleExpenseForm(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="e.g. 500"
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="expense-currency">Currency</label>
                <CurrencySelect
                  id="expense-currency"
                  value={vehicleExpenseForm.currency}
                  onChange={(value) => setVehicleExpenseForm(prev => ({ ...prev, currency: value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="expense-category">Category (optional)</label>
              <select
                id="expense-category"
                value={vehicleExpenseForm.category}
                onChange={(e) => setVehicleExpenseForm(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="">-- Select category --</option>
                {EXPENSE_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="expense-odometer">Odometer (km) - optional</label>
              <input
                type="number"
                id="expense-odometer"
                value={vehicleExpenseForm.odometer}
                onChange={(e) => setVehicleExpenseForm(prev => ({ ...prev, odometer: e.target.value }))}
                placeholder="e.g. 125000"
                step="1"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expense-receipt">Receipt photo (optional)</label>
              <div className="receipt-upload">
                <input
                  type="file"
                  id="expense-receipt"
                  accept="image/jpeg,image/png"
                  onChange={handleReceiptUpload}
                  disabled={uploadingReceipt}
                />
                {uploadingReceipt && <span className="upload-status">Uploading...</span>}
                {vehicleExpenseForm.receiptId && (
                  <span className="upload-status upload-status--success">
                    ✓ {vehicleExpenseForm.receiptFilename}
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="expense-notes">Notes (optional)</label>
              <textarea
                id="expense-notes"
                value={vehicleExpenseForm.notes}
                onChange={(e) => setVehicleExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <LocationInfo />

            <div className="form-actions">
              <button type="button" className="btn" onClick={closeForm}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || uploadingReceipt}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Custom Event Form */}
      {activeForm === 'custom' && (
        <Modal title="Log Custom Event" onClose={closeForm} variant="form">
          <form className="event-form" onSubmit={handleCustomEventSubmit}>
            <div className="form-group">
              <label htmlFor="custom-event-name">Event Name *</label>
              <input
                type="text"
                id="custom-event-name"
                value={customEventForm.eventName}
                onChange={(e) => setCustomEventForm(prev => ({ ...prev, eventName: e.target.value }))}
                placeholder="e.g. Arrived at campsite, Vehicle inspection"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="custom-notes">Notes (optional)</label>
              <textarea
                id="custom-notes"
                value={customEventForm.notes}
                onChange={(e) => setCustomEventForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>

            <LocationInfo />

            <div className="form-actions">
              <button type="button" className="btn" onClick={closeForm}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Grey Water Empty Confirmation */}
      {showGreyEmptyConfirm && (
        <Modal title="Empty Greywater" onClose={() => setShowGreyEmptyConfirm(false)} variant="form">
          <div className="event-form">
            <p style={{ textAlign: 'center', margin: '1rem 0' }}>Are you sure?</p>
            <div className="form-actions">
              <button type="button" className="btn" onClick={() => setShowGreyEmptyConfirm(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleGreyEmptyConfirm}>
                Confirm
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Undo Event Confirmation */}
      {undoConfirmEvent && (
        <Modal title="Undo Event" onClose={() => setUndoConfirmEvent(null)} variant="form">
          <div className="event-form">
            <p style={{ textAlign: 'center', margin: '1rem 0' }}>
              Delete this {formatEventType(undoConfirmEvent.event_type).toLowerCase()}?
            </p>
            <div className="form-actions">
              <button type="button" className="btn" onClick={() => setUndoConfirmEvent(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleUndo}
                disabled={undoing}
              >
                {undoing ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Event History Section */}
      <section className="event-history-section">
        <h2>Recent Events</h2>
        {loadingHistory ? (
          <div className="event-history-loading">Loading events...</div>
        ) : eventHistory.length === 0 ? (
          <div className="event-history-empty">No events logged yet.</div>
        ) : (
          <div className="event-history-list">
            {eventHistory.map((event) => (
              <div key={event.id} className="event-history-item">
                <div className="event-history-icon">{getEventIcon(event.event_type)}</div>
                <div className="event-history-content">
                  <div className="event-history-header">
                    <span className="event-history-type">{formatEventType(event.event_type)}</span>
                    <span className="event-history-time">{formatRelativeTime(event.timestamp)}</span>
                  </div>
                  <div className="event-history-details">
                    {getEventDetails(event)}
                  </div>
                </div>
                {isAdmin && isUndoable(event) && (
                  <button
                    className="btn-undo"
                    onClick={() => setUndoConfirmEvent(event)}
                    title="Undo this event"
                  >
                    Undo
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
